import { useMutation } from '@apollo/client';

import { PortfolioTemplateState } from '@generated/graphql';
import type {
  AssetClassBranch,
  PortfolioTemplate,
} from '@interfaces/financialProduct';
import type { TranslatedString } from '@interfaces/shared';
import { TranslatableTextField } from '@ovComponents/2-component/translatableTextField/translatableTextField';
import { AssetClassBranchesSelect } from '@ovComponents/3-pattern/assetClassBranchesSelect/assetClassBranchesSelect';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { UserContext } from '@providers/userContextProvider';
import { ovAnalyticsEvents as analyticsEvents } from '@utils/analytics/analytics';
import { Box, Button, Grid, Typography } from 'hephaestus/web';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../../hooks/useLocalization';
import {
  CREATE_PORTFOLIO_TEMPLATE,
  TRANSITION_PORTFOLIO_TEMPLATE,
  UPDATE_PORTFOLIO_TEMPLATE,
} from '../queries';

type PortfolioTemplateOptions = {
  enabled: boolean;
  canGoBack: boolean;
  required: FieldRequiredType.ERROR | FieldRequiredType.NOT_REQUIRED;
  label: TranslatedString;
  description: TranslatedString;
  templateName: {
    enabled: boolean;
    required: FieldRequiredType.ERROR | FieldRequiredType.NOT_REQUIRED;
    label: TranslatedString;
  };
  templateDescription: {
    enabled: boolean;
    required: FieldRequiredType.ERROR | FieldRequiredType.NOT_REQUIRED;
    label: TranslatedString;
  };
  assetClassAllocationEditor: {
    enabled: boolean;
    required: FieldRequiredType.ERROR | FieldRequiredType.NOT_REQUIRED;
    label: TranslatedString;
    assetClassAllocation: {
      enabled: boolean;
      required: FieldRequiredType.ERROR | FieldRequiredType.NOT_REQUIRED;
      label: TranslatedString;
    };
    targetPercentage: {
      enabled: boolean;
      required: FieldRequiredType.ERROR | FieldRequiredType.NOT_REQUIRED;
      label: TranslatedString;
    };
  };
  buttonSaveForLater?: {
    enabled: boolean;
    label: TranslatedString;
  };
  buttonCreate?: {
    enabled: boolean;
    label: TranslatedString;
  };
};

type CreatePortfolioTemplateProps = {
  options: PortfolioTemplateOptions;
  loading: boolean;
  workflowCompletion?: {
    workflow?: {
      id: string;
      name: string;
    };
    id: string;
    objectId: string;
    objectType: string;
  };
  onComplete: (template: PortfolioTemplate) => void;
  onSaveForLater?: (template: PortfolioTemplate) => void;
};

enum PortfolioTransition {
  activate = 'activate',
  draft = 'draft',
}

export const CreatePortfolioTemplate = ({
  options,
  loading,
  workflowCompletion,
  onComplete,
  onSaveForLater,
}: CreatePortfolioTemplateProps) => {
  const { translateBackendString, language } = useLocalization();
  const { t } = useTranslation(['shared', 'modelPortfolios']);
  const [focused, setFocused] = useState<string[]>([]);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { activeOrganization } = useContext(UserContext);
  const [portfolioTemplate, setPortfolioTemplate] = useState<PortfolioTemplate>(
    {
      id: '',
      name: '',
      translatedName: undefined,
      translatedTemplateDescription: undefined,
      assetClassBranches: [
        {
          id: '',
          assetClass: {
            id: '',
            name: '',
          },
          targetPercentage: 0,
          color: 'supportOne',
        },
      ],
      state: PortfolioTemplateState.Draft,
      createdAt: '',
      updatedAt: '',
    },
  );

  const validateTemplate = useCallback(() => {
    const requiredFields: string[] = [];

    if (
      options.templateName.required === FieldRequiredType.ERROR &&
      !portfolioTemplate?.name.trim()
    ) {
      requiredFields.push('templateName');
    }

    if (
      options.templateDescription.required === FieldRequiredType.ERROR &&
      !portfolioTemplate?.translatedTemplateDescription
    ) {
      requiredFields.push('templateDescription');
    }

    if (!portfolioTemplate?.assetClassBranches?.length) {
      requiredFields.push('assetClassBranches');
      return { isValid: false, requiredFields };
    }

    // Validate minimum number of branches
    if (portfolioTemplate.assetClassBranches.length < 1) {
      requiredFields.push('minimumBranches');
      return { isValid: false, requiredFields };
    }

    // Validate each branch has valid asset class and percentage
    const invalidBranches = portfolioTemplate.assetClassBranches.some(
      (branch) => {
        const hasValidAssetClass =
          branch.assetClass?.id && branch.assetClass?.id.trim() !== '';
        const hasValidPercentage =
          typeof branch.targetPercentage === 'number' &&
          branch.targetPercentage >= 0 &&
          branch.targetPercentage <= 100;
        return !hasValidAssetClass || !hasValidPercentage;
      },
    );

    if (invalidBranches) {
      requiredFields.push('invalidBranches');
      return { isValid: false, requiredFields };
    }

    const assetClassIds = portfolioTemplate.assetClassBranches.map(
      (branch) => branch.assetClass.id,
    );
    const uniqueAssetClassIds = new Set(assetClassIds);
    if (assetClassIds.length !== uniqueAssetClassIds.size) {
      requiredFields.push('duplicateAssetClasses');
      return { isValid: false, requiredFields };
    }

    const totalPercentage = portfolioTemplate.assetClassBranches.reduce(
      (sum, branch) => sum + (branch.targetPercentage || 0),
      0,
    );
    if (totalPercentage !== 100) {
      requiredFields.push('totalPercentage');
      return { isValid: false, requiredFields };
    }

    return { isValid: requiredFields.length === 0, requiredFields };
  }, [
    options.templateName.required,
    options.templateDescription.required,
    portfolioTemplate,
  ]);

  const [createPortfolioTemplate] = useMutation(CREATE_PORTFOLIO_TEMPLATE);
  const [updatePortfolioTemplate] = useMutation(UPDATE_PORTFOLIO_TEMPLATE);
  const [transitionPortfolioTemplate] = useMutation(
    TRANSITION_PORTFOLIO_TEMPLATE,
  );

  const isValid = useMemo(() => validateTemplate().isValid, [validateTemplate]);

  const createAndUpdateTemplate = async (transition: PortfolioTransition) => {
    const createResult = await createPortfolioTemplate({
      variables: {
        input: {
          name: portfolioTemplate?.name,
          translatedName: portfolioTemplate?.translatedName,
          translatedTemplateDescription:
            portfolioTemplate?.translatedTemplateDescription,
          organizationId: activeOrganization?.id,
        },
      },
    });

    const templateId =
      createResult.data.createPortfolioTemplate.portfolioTemplate.id;

    await updatePortfolioTemplate({
      variables: {
        input: {
          portfolioTemplateId: templateId,
          assetClassBranches: portfolioTemplate?.assetClassBranches?.map(
            (branch) => ({
              targetPercentage: branch.targetPercentage || 0,
              assetClassId: branch.assetClass.id,
            }),
          ),
        },
      },
    });

    const transitionResult = await transitionPortfolioTemplate({
      variables: {
        input: {
          portfolioTemplateId: templateId,
          transition,
        },
      },
    });

    return transitionResult.data.transitionPortfolioTemplate.portfolioTemplate;
  };

  const submit = async () => {
    const validationResult = validateTemplate();
    if (!validationResult.isValid) {
      setFocused((prev: string[]) => [
        ...prev,
        ...validationResult.requiredFields,
      ]);
      return;
    }

    try {
      sendAnalytic(
        analyticsEvents.workflowsCreateModelPortfolioContinueButtonSelect,
        {
          workflowStepTitle: options.label[language],
          workflowId: workflowCompletion?.workflow?.id,
          workflowName: workflowCompletion?.workflow?.name,
          activeWorkflowCompletionId: workflowCompletion?.id,
          objectId: workflowCompletion?.objectId,
          objectType: workflowCompletion?.objectType,
        },
      );

      const finalTemplate = await createAndUpdateTemplate(
        PortfolioTransition.activate,
      );

      // Track create and use template analytics
      sendAnalytic(
        analyticsEvents.modelPageCreateStep2BuildAssetTemplateCreateNewCreateUse,
        {
          templateName: translateBackendString(
            portfolioTemplate.translatedName,
          ),
          timestamp: new Date().toISOString(),
        },
      );

      await onComplete(finalTemplate);
    } catch {
      // Error is handled in the parent component
    }
  };

  const handleSaveForLater = async () => {
    if (!onSaveForLater) return;

    const validationResult = validateTemplate();
    if (!validationResult.isValid) {
      setFocused((prev: string[]) => [
        ...prev,
        ...validationResult.requiredFields,
      ]);
      return;
    }

    try {
      const finalTemplate = await createAndUpdateTemplate(
        PortfolioTransition.activate,
      );
      setPortfolioTemplate(finalTemplate);

      // Track save for later analytics
      sendAnalytic(
        analyticsEvents.modelPageCreateStep2BuildAssetTemplateCreateNewSaveForLater,
        {
          templateName: translateBackendString(
            portfolioTemplate.translatedName,
          ),
          timestamp: new Date().toISOString(),
        },
      );

      onSaveForLater(finalTemplate);
    } catch {
      // Error is handled in the parent component
    }
  };

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      testId='create-portfolio-template-form'
      submitButtonProps={{
        label:
          (options.buttonCreate?.enabled &&
            options.buttonCreate.label[language]) ||
          t('modelPortfolios:createAndUseTemplate'),
        disabled: loading || !isValid,
        dataTestId: 'create-and-use-template-button',
      }}
      buttonFooter={
        options.buttonSaveForLater?.enabled ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
              alignItems: 'flex-end',
            }}
          >
            <Button
              data-testid='save-for-later-template-button'
              onClick={handleSaveForLater}
              disabled={loading || !isValid}
              variant='contained'
              sx={{ mr: 2, textAlign: 'center' }}
              color='secondary'
            >
              {options.buttonSaveForLater.label[language] ||
                t('shared:saveForLater')}
            </Button>
          </Box>
        ) : undefined
      }
    >
      <Typography
        variant='displayLarge'
        sx={{
          mt: 1,
          mb: 2,
          fontWeight: 400,
          fontSize: '40px',
          fontFamily: 'Tiempos Text',
        }}
        data-testid='create-portfolio-template-title'
      >
        {options.label[language]}
        <Typography
          variant='bodyLarge'
          sx={{ mt: 1 }}
          data-testid='create-portfolio-template-description'
        >
          {options.description[language]}
        </Typography>
      </Typography>

      <Grid container spacing={2}>
        {options.templateName.enabled && (
          <Grid size={12}>
            <TranslatableTextField
              testId='create-portfolio-template-name'
              label={options.templateName.label[language]}
              value={portfolioTemplate?.translatedName || { [language]: '' }}
              onChange={(e: TranslatedString) =>
                setPortfolioTemplate({
                  ...portfolioTemplate,
                  name: e[language] || '',
                  translatedName: e,
                })
              }
              onBlur={() => setFocused([...focused, 'templateName'])}
              error={
                options.templateName.required === FieldRequiredType.ERROR &&
                !portfolioTemplate?.name &&
                focused.includes('templateName')
              }
              disabled={loading}
              fullWidth
              required={
                options.templateName.required === FieldRequiredType.ERROR
              }
            />
          </Grid>
        )}

        {options.templateDescription.enabled && (
          <Grid size={12}>
            <TranslatableTextField
              testId='template-description'
              label={options.templateDescription.label[language]}
              value={
                portfolioTemplate?.translatedTemplateDescription || {
                  [language]: '',
                }
              }
              onChange={(e: TranslatedString) =>
                setPortfolioTemplate({
                  ...portfolioTemplate,
                  translatedTemplateDescription: e,
                })
              }
              onBlur={() => setFocused([...focused, 'templateDescription'])}
              disabled={loading}
              fullWidth
              multiline
              rows={3}
              required={
                options.templateDescription.required === FieldRequiredType.ERROR
              }
            />
          </Grid>
        )}

        {options.assetClassAllocationEditor.enabled && (
          <Grid size={12}>
            <AssetClassBranchesSelect
              testId='create-portfolio-template-asset-class-allocation'
              assetClassBranches={portfolioTemplate?.assetClassBranches || []}
              setAssetClassBranches={(branches: AssetClassBranch[]) =>
                setPortfolioTemplate({
                  ...portfolioTemplate,
                  assetClassBranches: branches,
                })
              }
            />
          </Grid>
        )}
      </Grid>
    </WorkflowCompletionForm>
  );
};
